/**
 * Sacred Geometry Canvas
 * 
 * Interactive canvas for drawing and exploring sacred geometry patterns
 * - Flower of Life
 * - Metatron's Cube  
 * - Vesica Piscis
 * - Sri Yantra
 * - Golden Ratio Spiral
 * - Platonic Solids wireframes
 */

import React, { useRef, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

interface SacredGeometryCanvasProps {
  width?: number;
  height?: number;
  patternType?: string;
  color?: string;
  audioData?: { frequencyData: Uint8Array; averageAmplitude: number } | null;
  isPlaying?: boolean;
}

const SacredGeometryCanvas: React.FC<SacredGeometryCanvasProps> = ({
  width = 600,
  height = 600,
  patternType = 'flower-of-life',
  color = '#8b5cf6',
  audioData = null,
  isPlaying = false
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [zoom] = useState(1);
  const [rotation] = useState(0);
  const animationFrameRef = useRef<number | null>(null);

  /**
   * Draw Flower of Life (static)
   * Traditional complete pattern with 19 circles: 1 center + 6 inner ring + 12 outer ring
   */
  const drawFlowerOfLife = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number) => {
    const circleCount = 19; // Traditional Flower of Life has 19 circles
    
    // Center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();

    // First ring (6 circles)
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Second ring (12 circles) - Complete traditional pattern
    for (let i = 0; i < 12; i++) {
      const angle = (i * Math.PI * 2) / 12;
      const outerRadius = radius * Math.sqrt(3); // Distance to second ring
      
      const x = centerX + outerRadius * Math.cos(angle);
      const y = centerY + outerRadius * Math.sin(angle);
      
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
  };

  /**
   * Draw Flower of Life with audio reactivity
   */
  const drawFlowerOfLifeReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    // Center circle pulses with audio
    const centerRadius = radius * (1 + intensity * 0.3);
    ctx.beginPath();
    ctx.arc(centerX, centerY, centerRadius, 0, Math.PI * 2);
    ctx.stroke();

    // First ring (6 circles) - pulse outward
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6 + (intensity * 0.2); // Subtle rotation
      const ringRadius = radius * (1 + intensity * 0.2);
      const x = centerX + ringRadius * Math.cos(angle);
      const y = centerY + ringRadius * Math.sin(angle);
      
      ctx.beginPath();
      ctx.arc(x, y, ringRadius, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Second ring (12 circles) - expand with audio
    for (let i = 0; i < 12; i++) {
      const angle = (i * Math.PI * 2) / 12;
      const outerRadius = radius * Math.sqrt(3) * (1 + intensity * 0.15);
      const ringRadius = radius * (1 + intensity * 0.1);
      
      const x = centerX + outerRadius * Math.cos(angle);
      const y = centerY + outerRadius * Math.sin(angle);
      
      ctx.beginPath();
      ctx.arc(x, y, ringRadius, 0, Math.PI * 2);
      ctx.stroke();
    }
  };

  /**
   * Draw Vesica Piscis (static)
   */
  const drawVesicaPiscis = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number) => {
    // Two circles overlapping
    ctx.beginPath();
    ctx.arc(centerX - radius / 2, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(centerX + radius / 2, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();

    // Draw the vesica (almond shape) in the middle
    ctx.beginPath();
    ctx.strokeStyle = `${color}80`; // Semi-transparent
    ctx.lineWidth = 2;
    
    // Top point
    const vesicaHeight = radius * Math.sqrt(3);
    ctx.moveTo(centerX, centerY - vesicaHeight / 2);
    ctx.lineTo(centerX, centerY + vesicaHeight / 2);
    ctx.stroke();
  };

  /**
   * Draw Vesica Piscis with audio reactivity
   */
  const drawVesicaPiscisReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.3);
    
    // Two circles overlapping - pulse outward
    ctx.beginPath();
    ctx.arc(centerX - pulseRadius / 2, centerY, pulseRadius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(centerX + pulseRadius / 2, centerY, pulseRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Vesica shape pulses
    const vesicaHeight = pulseRadius * Math.sqrt(3);
    ctx.beginPath();
    ctx.moveTo(centerX, centerY - vesicaHeight / 2);
    ctx.lineTo(centerX, centerY + vesicaHeight / 2);
    ctx.stroke();
  };

  /**
   * Draw Golden Spiral (static)
   */
  const drawGoldenSpiral = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, size: number) => {
    const phi = 1.618033988749895;
    let a = size / 50; // Starting size
    const b = Math.log(phi) / (Math.PI / 2);

    ctx.beginPath();
    
    for (let angle = 0; angle < 6 * Math.PI; angle += 0.1) {
      const r = a * Math.exp(b * angle);
      const x = centerX + r * Math.cos(angle);
      const y = centerY + r * Math.sin(angle);
      
      if (angle === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    
    ctx.stroke();

    // Draw golden rectangles
    ctx.strokeStyle = `${color}40`;
    let rectSize = a;
    let x = centerX;
    let y = centerY;
    
    for (let i = 0; i < 8; i++) {
      ctx.strokeRect(x, y, rectSize, rectSize * phi);
      
      // Move to next position
      y += rectSize * phi;
      rectSize *= phi;
    }
  };

  /**
   * Draw Golden Spiral with audio reactivity
   */
  const drawGoldenSpiralReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, size: number, intensity: number) => {
    const phi = 1.618033988749895;
    let a = size / 50 * (1 + intensity * 0.2);
    const b = Math.log(phi) / (Math.PI / 2);

    ctx.beginPath();
    
    // Spiral rotates and expands with audio
    const rotationOffset = intensity * 0.5;
    for (let angle = 0; angle < 6 * Math.PI; angle += 0.1) {
      const r = a * Math.exp(b * (angle + rotationOffset));
      const x = centerX + r * Math.cos(angle);
      const y = centerY + r * Math.sin(angle);
      
      if (angle === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    
    ctx.stroke();
  };

  /**
   * Draw Metatron's Cube (static)
   */
  const drawMetatronsCube = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number) => {
    // 13 circles in Fruit of Life pattern
    const circles: { x: number; y: number }[] = [
      { x: centerX, y: centerY } // Center
    ];

    // Inner 6 circles
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6;
      circles.push({
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle)
      });
    }

    // Outer 6 circles
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6;
      const outerRadius = radius * Math.sqrt(3);
      circles.push({
        x: centerX + outerRadius * Math.cos(angle),
        y: centerY + outerRadius * Math.sin(angle)
      });
    }

    // Draw all circles
    circles.forEach(circle => {
      ctx.beginPath();
      ctx.arc(circle.x, circle.y, radius / 2, 0, Math.PI * 2);
      ctx.strokeStyle = `${color}40`;
      ctx.stroke();
    });

    // Connect all circles to all other circles (78 lines total)
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    
    for (let i = 0; i < circles.length; i++) {
      for (let j = i + 1; j < circles.length; j++) {
        ctx.beginPath();
        ctx.moveTo(circles[i].x, circles[i].y);
        ctx.lineTo(circles[j].x, circles[j].y);
        ctx.stroke();
      }
    }
  };

  /**
   * Draw Metatron's Cube with audio reactivity
   */
  const drawMetatronsCubeReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.2);
    const circles: { x: number; y: number }[] = [
      { x: centerX, y: centerY } // Center
    ];

    // Inner 6 circles - pulse and rotate
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6 + (intensity * 0.2);
      circles.push({
        x: centerX + pulseRadius * Math.cos(angle),
        y: centerY + pulseRadius * Math.sin(angle)
      });
    }

    // Outer 6 circles - expand
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6;
      const outerRadius = pulseRadius * Math.sqrt(3) * (1 + intensity * 0.15);
      circles.push({
        x: centerX + outerRadius * Math.cos(angle),
        y: centerY + outerRadius * Math.sin(angle)
      });
    }

    // Draw all circles with pulsing
    circles.forEach((circle, index) => {
      const circleRadius = (pulseRadius / 2) * (1 + (index === 0 ? intensity * 0.3 : intensity * 0.1));
      ctx.beginPath();
      ctx.arc(circle.x, circle.y, circleRadius, 0, Math.PI * 2);
      ctx.stroke();
    });

    // Connect lines pulse with intensity
    for (let i = 0; i < circles.length; i++) {
      for (let j = i + 1; j < circles.length; j++) {
        ctx.beginPath();
        ctx.moveTo(circles[i].x, circles[i].y);
        ctx.lineTo(circles[j].x, circles[j].y);
        ctx.stroke();
      }
    }
  };

  /**
   * Draw Sri Yantra (static)
   */
  const drawSriYantra = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, size: number) => {
    // Outer square (bhupura)
    ctx.strokeRect(centerX - size, centerY - size, size * 2, size * 2);

    // 16-petal lotus
    for (let i = 0; i < 16; i++) {
      const angle = (i * Math.PI * 2) / 16;
      const x1 = centerX + size * 0.9 * Math.cos(angle);
      const y1 = centerY + size * 0.9 * Math.sin(angle);
      const x2 = centerX + size * 1.1 * Math.cos(angle + Math.PI / 32);
      const y2 = centerY + size * 1.1 * Math.sin(angle + Math.PI / 32);
      
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x1, y1);
      ctx.quadraticCurveTo(x2, y2, centerX, centerY);
      ctx.stroke();
    }

    /**
     * 9 interlocking triangles (simplified representation)
     * 
     * NOTE: This is an artistic approximation. Traditional Sri Yantra requires
     * precise geometric construction of 43 small triangles formed by 9 interlocking
     * triangles with specific angle relationships and intersection points.
     * Authentic construction requires solving complex geometric equations.
     * This implementation uses concentric scaling for visual effect.
     */
    // 4 upward pointing (Shiva - masculine)
    for (let i = 0; i < 4; i++) {
      const scale = 0.8 - i * 0.15;
      drawTriangle(ctx, centerX, centerY, size * scale, 0);
    }

    // 5 downward pointing (Shakti - feminine)
    for (let i = 0; i < 5; i++) {
      const scale = 0.7 - i * 0.12;
      drawTriangle(ctx, centerX, centerY, size * scale, Math.PI);
    }

    // Bindu (central point)
    ctx.beginPath();
    ctx.arc(centerX, centerY, 4, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  };

  /**
   * Draw Sri Yantra with audio reactivity
   */
  const drawSriYantraReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, size: number, intensity: number) => {
    const pulseSize = size * (1 + intensity * 0.2);
    
    // Outer square pulses
    ctx.strokeRect(centerX - pulseSize, centerY - pulseSize, pulseSize * 2, pulseSize * 2);

    // 16-petal lotus rotates and expands
    for (let i = 0; i < 16; i++) {
      const angle = (i * Math.PI * 2) / 16 + (intensity * 0.3);
      const x1 = centerX + pulseSize * 0.9 * Math.cos(angle);
      const y1 = centerY + pulseSize * 0.9 * Math.sin(angle);
      const x2 = centerX + pulseSize * 1.1 * Math.cos(angle + Math.PI / 32);
      const y2 = centerY + pulseSize * 1.1 * Math.sin(angle + Math.PI / 32);
      
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x1, y1);
      ctx.quadraticCurveTo(x2, y2, centerX, centerY);
      ctx.stroke();
    }

    // Triangles pulse
    for (let i = 0; i < 4; i++) {
      const scale = (0.8 - i * 0.15) * (1 + intensity * 0.15);
      drawTriangle(ctx, centerX, centerY, pulseSize * scale, intensity * 0.2);
    }

    for (let i = 0; i < 5; i++) {
      const scale = (0.7 - i * 0.12) * (1 + intensity * 0.15);
      drawTriangle(ctx, centerX, centerY, pulseSize * scale, Math.PI + intensity * 0.2);
    }

    // Bindu pulses
    const binduSize = 4 * (1 + intensity * 0.5);
    ctx.beginPath();
    ctx.arc(centerX, centerY, binduSize, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  };

  /**
   * Draw Hexagon with audio reactivity
   */
  const drawHexagonReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.2);
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6 + (intensity * 0.2); // 60° intervals (π/3)
      const x = centerX + pulseRadius * Math.cos(angle);
      const y = centerY + pulseRadius * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
    
    // Inner hexagon
    const innerRadius = pulseRadius * 0.6 * (1 + intensity * 0.15);
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6; // 60° intervals (π/3)
      const x = centerX + innerRadius * Math.cos(angle);
      const y = centerY + innerRadius * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
  };

  /**
   * Draw Pentagon with audio reactivity
   */
  const drawPentagonReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.25);
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const angle = (i * Math.PI * 2) / 5 - Math.PI / 2 + (intensity * 0.3);
      const x = centerX + pulseRadius * Math.cos(angle);
      const y = centerY + pulseRadius * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
    
    // Inner pentagon
    const innerRadius = pulseRadius * 0.618; // Golden ratio
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const angle = (i * Math.PI * 2) / 5 - Math.PI / 2;
      const x = centerX + innerRadius * Math.cos(angle);
      const y = centerY + innerRadius * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
  };

  /**
   * Draw Torus with audio reactivity
   */
  const drawTorusReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.2);
    // Draw outer ring
    ctx.beginPath();
    ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
    ctx.stroke();
    
    // Draw inner ring
    const innerRadius = pulseRadius * 0.5 * (1 + intensity * 0.15);
    ctx.beginPath();
    ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
    ctx.stroke();
    
    // Draw connecting lines (torus structure)
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI * 2) / 8 + (intensity * 0.4);
      const x1 = centerX + innerRadius * Math.cos(angle);
      const y1 = centerY + innerRadius * Math.sin(angle);
      const x2 = centerX + pulseRadius * Math.cos(angle);
      const y2 = centerY + pulseRadius * Math.sin(angle);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }
  };

  /**
   * Draw Mandala with audio reactivity
   */
  const drawMandalaReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.15);
    // Draw concentric circles
    for (let i = 1; i <= 4; i++) {
      const r = (pulseRadius * i) / 4;
      ctx.beginPath();
      ctx.arc(centerX, centerY, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Draw radial lines (8-fold symmetry)
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI * 2) / 8 + (intensity * 0.25);
      const x = centerX + pulseRadius * Math.cos(angle);
      const y = centerY + pulseRadius * Math.sin(angle);
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    
    // Center point pulses
    const centerSize = 8 * (1 + intensity * 0.5);
    ctx.beginPath();
    ctx.arc(centerX, centerY, centerSize, 0, Math.PI * 2);
    ctx.fill();
  };

  /**
   * Draw Chladni Hexagon with audio reactivity
   */
  const drawChladniHexagonReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.2);
    // Draw hexagon
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6; // 60° intervals (π/3)
      const x = centerX + pulseRadius * Math.cos(angle);
      const y = centerY + pulseRadius * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
    
    // Draw nodal lines (Chladni pattern)
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6 + (intensity * 0.15); // 60° intervals (π/3)
      const x = centerX + pulseRadius * 0.7 * Math.cos(angle);
      const y = centerY + pulseRadius * 0.7 * Math.sin(angle);
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    
    // Center node pulses
    const nodeSize = 6 * (1 + intensity * 0.4);
    ctx.beginPath();
    ctx.arc(centerX, centerY, nodeSize, 0, Math.PI * 2);
    ctx.fill();
  };

  /**
   * Draw Star Pentagram with audio reactivity
   */
  const drawStarPentagramReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.25);
    const points: { x: number; y: number }[] = [];
    
    // Calculate 5 outer points
    for (let i = 0; i < 5; i++) {
      const angle = (i * Math.PI * 2) / 5 - Math.PI / 2 + (intensity * 0.3);
      points.push({
        x: centerX + pulseRadius * Math.cos(angle),
        y: centerY + pulseRadius * Math.sin(angle)
      });
    }
    
    // Draw star (connect every other point)
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    ctx.lineTo(points[2].x, points[2].y);
    ctx.lineTo(points[4].x, points[4].y);
    ctx.lineTo(points[1].x, points[1].y);
    ctx.lineTo(points[3].x, points[3].y);
    ctx.closePath();
    ctx.stroke();
    
    // Draw inner pentagon
    const innerRadius = pulseRadius * 0.382; // Golden ratio
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const angle = (i * Math.PI * 2) / 5 - Math.PI / 2;
      const x = centerX + innerRadius * Math.cos(angle);
      const y = centerY + innerRadius * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
  };

  /**
   * Draw Spiral Galaxy with audio reactivity
   */
  const drawSpiralGalaxyReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, size: number, intensity: number) => {
    const phi = 1.618033988749895;
    let a = size / 50 * (1 + intensity * 0.2);
    const b = Math.log(phi) / (Math.PI / 2);
    
    ctx.beginPath();
    const rotationOffset = intensity * 0.6;
    for (let angle = 0; angle < 8 * Math.PI; angle += 0.1) {
      const r = a * Math.exp(b * (angle + rotationOffset));
      const x = centerX + r * Math.cos(angle);
      const y = centerY + r * Math.sin(angle);
      if (angle === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    
    // Draw spiral arms
    for (let arm = 0; arm < 2; arm++) {
      ctx.beginPath();
      const armOffset = arm * Math.PI;
      for (let angle = 0; angle < 4 * Math.PI; angle += 0.15) {
        const r = a * Math.exp(b * (angle + rotationOffset + armOffset)) * 0.6;
        const x = centerX + r * Math.cos(angle + armOffset);
        const y = centerY + r * Math.sin(angle + armOffset);
        if (angle === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
  };

  /**
   * Draw Tree of Life with audio reactivity
   * 
   * Correct Kabbalistic Tree of Life structure:
   * - 10 Sephiroth arranged in 4 levels
   * - 22 paths connecting them (corresponding to 22 Hebrew letters)
   * - Three pillars: Right (Mercy), Left (Severity), Middle (Balance)
   * 
   * Sephiroth indices:
   * 0: Kether (Crown) - Top center
   * 1: Chokmah (Wisdom) - Top right (Pillar of Mercy)
   * 2: Binah (Understanding) - Top left (Pillar of Severity)
   * 3: Chesed (Mercy) - Right, level 2
   * 4: Geburah (Severity) - Left, level 2
   * 5: Tiphareth (Beauty) - Center, level 2 (Pillar of Balance)
   * 6: Netzach (Victory) - Right, level 3
   * 7: Hod (Glory) - Left, level 3
   * 8: Yesod (Foundation) - Center, level 3 (Pillar of Balance)
   * 9: Malkuth (Kingdom) - Bottom center
   */
  const drawTreeOfLifeReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.15);
    const sephiroth: { x: number; y: number; name: string; pillar: 'right' | 'left' | 'middle' }[] = [];
    
    // Calculate proper spacing for Tree of Life
    const verticalSpacing = pulseRadius * 0.35;
    const horizontalSpacing = pulseRadius * 0.45;
    const topY = centerY - pulseRadius * 0.75;
    
    // Level 1: Kether (Crown) - Top center
    sephiroth.push({ x: centerX, y: topY, name: 'Kether', pillar: 'middle' });
    
    // Level 2: Chokmah (Wisdom) - Right, Binah (Understanding) - Left
    sephiroth.push({ x: centerX + horizontalSpacing, y: topY + verticalSpacing, name: 'Chokmah', pillar: 'right' });
    sephiroth.push({ x: centerX - horizontalSpacing, y: topY + verticalSpacing, name: 'Binah', pillar: 'left' });
    
    // Level 3: Chesed (Mercy) - Right, Geburah (Severity) - Left, Tiphareth (Beauty) - Center
    sephiroth.push({ x: centerX + horizontalSpacing, y: topY + verticalSpacing * 2, name: 'Chesed', pillar: 'right' });
    sephiroth.push({ x: centerX - horizontalSpacing, y: topY + verticalSpacing * 2, name: 'Geburah', pillar: 'left' });
    sephiroth.push({ x: centerX, y: topY + verticalSpacing * 2, name: 'Tiphareth', pillar: 'middle' });
    
    // Level 4: Netzach (Victory) - Right, Hod (Glory) - Left, Yesod (Foundation) - Center
    sephiroth.push({ x: centerX + horizontalSpacing, y: topY + verticalSpacing * 3, name: 'Netzach', pillar: 'right' });
    sephiroth.push({ x: centerX - horizontalSpacing, y: topY + verticalSpacing * 3, name: 'Hod', pillar: 'left' });
    sephiroth.push({ x: centerX, y: topY + verticalSpacing * 3, name: 'Yesod', pillar: 'middle' });
    
    // Level 5: Malkuth (Kingdom) - Bottom center
    sephiroth.push({ x: centerX, y: topY + verticalSpacing * 4, name: 'Malkuth', pillar: 'middle' });
    
    // Draw all 22 paths (correct Tree of Life connections)
    // These correspond to the 22 Hebrew letters and the paths of initiation
    // Traditional Tree of Life has exactly 22 paths connecting the 10 sephiroth
    const paths = [
      // Paths from Kether (0) - 3 paths
      [0, 1], // Kether -> Chokmah (Path 11: Aleph - א)
      [0, 2], // Kether -> Binah (Path 12: Beth - ב)
      [0, 5], // Kether -> Tiphareth (Path 13: Gimel - ג)
      
      // Paths from Chokmah (1) - 3 paths
      [1, 2], // Chokmah -> Binah (Path 14: Daleth - ד)
      [1, 3], // Chokmah -> Chesed (Path 15: He - ה)
      [1, 5], // Chokmah -> Tiphareth (Path 16: Vau - ו)
      
      // Paths from Binah (2) - 2 paths
      [2, 4], // Binah -> Geburah (Path 17: Zayin - ז)
      [2, 5], // Binah -> Tiphareth (Path 18: Cheth - ח)
      
      // Paths from Chesed (3) - 3 paths
      [3, 4], // Chesed -> Geburah (Path 19: Teth - ט)
      [3, 5], // Chesed -> Tiphareth (Path 20: Yod - י)
      [3, 6], // Chesed -> Netzach (Path 21: Kaph - כ)
      
      // Paths from Geburah (4) - 2 paths
      [4, 5], // Geburah -> Tiphareth (Path 22: Lamed - ל)
      [4, 7], // Geburah -> Hod (Path 23: Mem - מ)
      
      // Paths from Tiphareth (5) - 3 paths
      [5, 6], // Tiphareth -> Netzach (Path 24: Nun - נ)
      [5, 7], // Tiphareth -> Hod (Path 25: Samekh - ס)
      [5, 8], // Tiphareth -> Yesod (Path 26: Ayin - ע)
      
      // Paths from Netzach (6) - 3 paths
      [6, 7], // Netzach -> Hod (Path 27: Pe - פ)
      [6, 8], // Netzach -> Yesod (Path 28: Tzaddi - צ)
      [6, 9], // Netzach -> Malkuth (Path 29: Qoph - ק)
      
      // Paths from Hod (7) - 2 paths
      [7, 8], // Hod -> Yesod (Path 30: Resh - ר)
      [7, 9], // Hod -> Malkuth (Path 31: Shin - ש)
      
      // Paths from Yesod (8) - 1 path
      [8, 9], // Yesod -> Malkuth (Path 32: Tav - ת)
    ];
    
    // Draw paths first (so sephiroth appear on top)
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    paths.forEach(([from, to]) => {
      if (sephiroth[from] && sephiroth[to]) {
        ctx.beginPath();
        ctx.moveTo(sephiroth[from].x, sephiroth[from].y);
        ctx.lineTo(sephiroth[to].x, sephiroth[to].y);
        ctx.stroke();
      }
    });
    
    // Draw sephiroth (circles) with audio reactivity
    sephiroth.forEach((sep, index) => {
      const baseRadius = 14;
      const pulseMultiplier = index === 0 || index === 9 ? 0.5 : 0.3; // Kether and Malkuth pulse more
      const sepRadius = baseRadius * (1 + intensity * pulseMultiplier);
      
      // Color by pillar
      if (sep.pillar === 'right') {
        ctx.strokeStyle = '#10b981'; // Emerald for Mercy pillar
      } else if (sep.pillar === 'left') {
        ctx.strokeStyle = '#ef4444'; // Red for Severity pillar
      } else {
        ctx.strokeStyle = color; // Purple for Balance pillar
      }
      
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(sep.x, sep.y, sepRadius, 0, Math.PI * 2);
      ctx.stroke();
      
      // Optional: Draw sephirah name (small text)
      if (zoom > 1.5) {
        ctx.fillStyle = ctx.strokeStyle;
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(sep.name, sep.x, sep.y + sepRadius + 12);
      }
    });
  };

  /**
   * Helper: Draw triangle
   */
  const drawTriangle = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number, rotation: number) => {
    ctx.beginPath();
    for (let i = 0; i < 3; i++) {
      const angle = rotation + (i * Math.PI * 2) / 3 - Math.PI / 2;
      const px = x + size * Math.cos(angle);
      const py = y + size * Math.sin(angle);
      
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.stroke();
  };

  /**
   * Draw Seed of Life (static)
   */
  const drawSeedOfLife = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number) => {
    // Center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();

    // 6 surrounding circles
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
  };

  /**
   * Draw Seed of Life with audio reactivity
   */
  const drawSeedOfLifeReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    // Center circle pulses
    const centerRadius = radius * (1 + intensity * 0.4);
    ctx.beginPath();
    ctx.arc(centerX, centerY, centerRadius, 0, Math.PI * 2);
    ctx.stroke();

    // 6 surrounding circles - expand and rotate
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6 + (intensity * 0.3);
      const ringRadius = radius * (1 + intensity * 0.25);
      const x = centerX + ringRadius * Math.cos(angle);
      const y = centerY + ringRadius * Math.sin(angle);
      
      ctx.beginPath();
      ctx.arc(x, y, ringRadius, 0, Math.PI * 2);
      ctx.stroke();
    }
  };

  /**
   * Draw Egg of Life with audio reactivity
   */
  const drawEggOfLifeReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    // 7 circles in specific arrangement (from Seed of Life)
    const centerRadius = radius * (1 + intensity * 0.3);
    ctx.beginPath();
    ctx.arc(centerX, centerY, centerRadius, 0, Math.PI * 2);
    ctx.stroke();

    // 6 surrounding circles - arranged to form egg shape
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6 + (intensity * 0.2);
      const ringRadius = radius * (1 + intensity * 0.2);
      const x = centerX + ringRadius * Math.cos(angle);
      const y = centerY + ringRadius * Math.sin(angle);
      
      ctx.beginPath();
      ctx.arc(x, y, ringRadius, 0, Math.PI * 2);
      ctx.stroke();
    }
  };

  /**
   * Draw Fruit of Life with audio reactivity
   */
  const drawFruitOfLifeReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    // 13 circles from Flower of Life
    const pulseRadius = radius * (1 + intensity * 0.15);
    
    // Center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Inner 6 circles
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6 + (intensity * 0.15);
      const x = centerX + pulseRadius * Math.cos(angle);
      const y = centerY + pulseRadius * Math.sin(angle);
      ctx.beginPath();
      ctx.arc(x, y, pulseRadius, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Outer 6 circles
    const outerRadius = pulseRadius * Math.sqrt(3);
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6;
      const x = centerX + outerRadius * Math.cos(angle);
      const y = centerY + outerRadius * Math.sin(angle);
      ctx.beginPath();
      ctx.arc(x, y, pulseRadius, 0, Math.PI * 2);
      ctx.stroke();
    }
  };

  /**
   * Draw Merkaba with audio reactivity
   */
  const drawMerkabaReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.2);
    
    // Two interlocking tetrahedra (star tetrahedron) - 8 vertices
    const vertices: { x: number; y: number }[] = [];
    
    // Upper tetrahedron (4 vertices)
    vertices.push({ x: centerX, y: centerY - pulseRadius * 0.8 }); // Top
    vertices.push({ x: centerX - pulseRadius * 0.6, y: centerY + pulseRadius * 0.4 }); // Bottom left
    vertices.push({ x: centerX + pulseRadius * 0.6, y: centerY + pulseRadius * 0.4 }); // Bottom right
    vertices.push({ x: centerX, y: centerY + pulseRadius * 0.2 }); // Center front
    
    // Lower tetrahedron (4 vertices) - rotated
    vertices.push({ x: centerX, y: centerY + pulseRadius * 0.8 }); // Bottom
    vertices.push({ x: centerX - pulseRadius * 0.6, y: centerY - pulseRadius * 0.4 }); // Top left
    vertices.push({ x: centerX + pulseRadius * 0.6, y: centerY - pulseRadius * 0.4 }); // Top right
    vertices.push({ x: centerX, y: centerY - pulseRadius * 0.2 }); // Center back
    
    // Draw vertices
    vertices.forEach((v, i) => {
      const nodeSize = 6 * (1 + intensity * 0.3);
      ctx.beginPath();
      ctx.arc(v.x, v.y, nodeSize, 0, Math.PI * 2);
      ctx.fill();
    });
    
    // Draw interlocking lines (simplified)
    const connections = [
      [0, 1], [0, 2], [0, 3], [1, 2], [1, 3], [2, 3], // Upper tetrahedron
      [4, 5], [4, 6], [4, 7], [5, 6], [5, 7], [6, 7], // Lower tetrahedron
      [0, 4], [1, 5], [2, 6], [3, 7] // Interlocking connections
    ];
    
    connections.forEach(([from, to]) => {
      ctx.beginPath();
      ctx.moveTo(vertices[from].x, vertices[from].y);
      ctx.lineTo(vertices[to].x, vertices[to].y);
      ctx.stroke();
    });
  };

  /**
   * Draw Octagon with audio reactivity
   */
  const drawOctagonReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.2);
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI * 2) / 8 + (intensity * 0.2);
      const x = centerX + pulseRadius * Math.cos(angle);
      const y = centerY + pulseRadius * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
    
    // Inner octagon
    const innerRadius = pulseRadius * 0.7;
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI * 2) / 8;
      const x = centerX + innerRadius * Math.cos(angle);
      const y = centerY + innerRadius * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
  };

  /**
   * Draw Dodecagon with audio reactivity
   */
  const drawDodecagonReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.2);
    ctx.beginPath();
    for (let i = 0; i < 12; i++) {
      const angle = (i * Math.PI * 2) / 12 + (intensity * 0.15);
      const x = centerX + pulseRadius * Math.cos(angle);
      const y = centerY + pulseRadius * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
    
    // Inner dodecagon
    const innerRadius = pulseRadius * 0.75;
    ctx.beginPath();
    for (let i = 0; i < 12; i++) {
      const angle = (i * Math.PI * 2) / 12;
      const x = centerX + innerRadius * Math.cos(angle);
      const y = centerY + innerRadius * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
  };

  /**
   * Draw Chladni (1,1) pattern with audio reactivity
   */
  const drawChladni11Reactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.2);
    
    // Single nodal line (vertical)
    ctx.beginPath();
    ctx.moveTo(centerX, centerY - pulseRadius);
    ctx.lineTo(centerX, centerY + pulseRadius);
    ctx.stroke();
    
    // Outer boundary
    ctx.beginPath();
    ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
    ctx.stroke();
  };

  /**
   * Draw Chladni (2,2) pattern with audio reactivity
   */
  const drawChladni22Reactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.2);
    
    // Four-quadrant nodal lines
    ctx.beginPath();
    ctx.moveTo(centerX, centerY - pulseRadius);
    ctx.lineTo(centerX, centerY + pulseRadius);
    ctx.moveTo(centerX - pulseRadius, centerY);
    ctx.lineTo(centerX + pulseRadius, centerY);
    ctx.stroke();
    
    // Outer boundary
    ctx.beginPath();
    ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
    ctx.stroke();
  };

  /**
   * Draw Chladni (3,3) pattern with audio reactivity
   */
  const drawChladni33Reactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.2);
    
    // Nine-section nodal pattern
    const spacing = pulseRadius / 3;
    for (let i = -1; i <= 1; i++) {
      ctx.beginPath();
      ctx.moveTo(centerX + i * spacing, centerY - pulseRadius);
      ctx.lineTo(centerX + i * spacing, centerY + pulseRadius);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(centerX - pulseRadius, centerY + i * spacing);
      ctx.lineTo(centerX + pulseRadius, centerY + i * spacing);
      ctx.stroke();
    }
    
    // Outer boundary
    ctx.beginPath();
    ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
    ctx.stroke();
  };

  /**
   * Draw Chladni Radial pattern with audio reactivity
   */
  const drawChladniRadialReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.2);
    
    // Concentric circular nodal lines
    for (let i = 1; i <= 4; i++) {
      const r = (pulseRadius * i) / 4;
      ctx.beginPath();
      ctx.arc(centerX, centerY, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Outer boundary
    ctx.beginPath();
    ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
    ctx.stroke();
  };

  /**
   * Draw Solfeggio frequency pattern with audio reactivity
   */
  const drawSolfeggioReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number, patternType: string) => {
    const pulseRadius = radius * (1 + intensity * 0.25);
    
    // Extract frequency from pattern type
    const freqMatch = patternType.match(/\d+/);
    const freq = freqMatch ? parseInt(freqMatch[0]) : 528;
    
    // Draw concentric circles representing frequency waves
    const waveCount = Math.min(8, Math.floor(freq / 100));
    for (let i = 1; i <= waveCount; i++) {
      const r = (pulseRadius * i) / waveCount;
      ctx.beginPath();
      ctx.arc(centerX, centerY, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Center point pulses
    const centerSize = 8 * (1 + intensity * 0.5);
    ctx.beginPath();
    ctx.arc(centerX, centerY, centerSize, 0, Math.PI * 2);
    ctx.fill();
  };

  /**
   * Draw Fibonacci Spiral with audio reactivity
   */
  const drawFibonacciSpiralReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, size: number, intensity: number) => {
    const phi = 1.618033988749895;
    let a = size / 50 * (1 + intensity * 0.2);
    const b = Math.log(phi) / (Math.PI / 2);
    
    ctx.beginPath();
    const rotationOffset = intensity * 0.5;
    for (let angle = 0; angle < 6 * Math.PI; angle += 0.1) {
      const r = a * Math.exp(b * (angle + rotationOffset));
      const x = centerX + r * Math.cos(angle);
      const y = centerY + r * Math.sin(angle);
      if (angle === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  };

  /**
   * Draw Snowflake with audio reactivity
   */
  const drawSnowflakeReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.2);
    
    // 6-fold symmetry with branching
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6 + (intensity * 0.2);
      
      // Main branch
      const x1 = centerX + pulseRadius * 0.8 * Math.cos(angle);
      const y1 = centerY + pulseRadius * 0.8 * Math.sin(angle);
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x1, y1);
      ctx.stroke();
      
      // Side branches
      for (let j = 0; j < 2; j++) {
        const branchAngle = angle + (j === 0 ? -0.3 : 0.3);
        const branchLength = pulseRadius * 0.4;
        const x2 = centerX + branchLength * Math.cos(branchAngle);
        const y2 = centerY + branchLength * Math.sin(branchAngle);
        ctx.beginPath();
        ctx.moveTo(centerX + pulseRadius * 0.4 * Math.cos(angle), centerY + pulseRadius * 0.4 * Math.sin(angle));
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    }
  };

  /**
   * Draw Water Cymatics with audio reactivity
   */
  const drawWaterCymaticsReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.3);
    
    // Fluid wave patterns - concentric ripples
    for (let i = 1; i <= 6; i++) {
      const r = (pulseRadius * i) / 6;
      const waveIntensity = Math.sin(i * 0.5 + intensity * 2) * 0.1;
      ctx.beginPath();
      ctx.arc(centerX, centerY, r * (1 + waveIntensity), 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Radial wave lines
    for (let i = 0; i < 12; i++) {
      const angle = (i * Math.PI * 2) / 12 + (intensity * 0.3);
      const x = centerX + pulseRadius * Math.cos(angle);
      const y = centerY + pulseRadius * Math.sin(angle);
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  /**
   * Draw Yin-Yang with audio reactivity
   * Accurate Taijitu symbol:
   * - Yin (black) on RIGHT side
   * - Yang (white) on LEFT side  
   * - S-curve created by two semicircles of radius R/2
   * - Yin eye (black) in Yang area (top)
   * - Yang eye (white) in Yin area (bottom)
   */
  const drawYinYangReactive = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, intensity: number) => {
    const pulseRadius = radius * (1 + intensity * 0.2);
    const eyeRadius = pulseRadius / 6;
    const smallRadius = pulseRadius / 2;
    
    // Save context
    ctx.save();
    
    // Set colors
    const yinColor = '#000000'; // Black for Yin
    const yangColor = '#FFFFFF'; // White for Yang

    // Base: full Yang (white) circle
    ctx.fillStyle = yangColor;
    ctx.beginPath();
    ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
    ctx.fill();
    
    // Yin body: right semicircle
    ctx.fillStyle = yinColor;
    ctx.beginPath();
    ctx.arc(centerX, centerY, pulseRadius, -Math.PI / 2, Math.PI / 2, false);
    ctx.lineTo(centerX, centerY - pulseRadius);
    ctx.closePath();
    ctx.fill();

    // Top half-circle (black) and bottom half-circle (white) create the true S-curve seam
    ctx.fillStyle = yinColor;
    ctx.beginPath();
    ctx.arc(centerX, centerY - smallRadius, smallRadius, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.fillStyle = yangColor;
    ctx.beginPath();
    ctx.arc(centerX, centerY + smallRadius, smallRadius, 0, Math.PI * 2);
    ctx.fill();
    
    // Inverted dot in top lobe (white dot inside black curve)
    ctx.fillStyle = yangColor;
    ctx.beginPath();
    ctx.arc(centerX, centerY - smallRadius, eyeRadius, 0, Math.PI * 2);
    ctx.fill();
    
    // Inverted dot in bottom lobe (black dot inside white curve)
    ctx.fillStyle = yinColor;
    ctx.beginPath();
    ctx.arc(centerX, centerY + smallRadius, eyeRadius, 0, Math.PI * 2);
    ctx.fill();

    // Outer circle border on top to keep edge crisp
    ctx.beginPath();
    ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
    ctx.stroke();
    
    ctx.restore();
  };

  /**
   * Convert color to rgba with alpha
   */
  const colorToRgba = (colorStr: string, alpha: number): string => {
    // If already rgba, replace alpha
    if (colorStr.startsWith('rgba')) {
      return colorStr.replace(/[\d\.]+\)$/g, `${alpha})`);
    }
    // If rgb, convert to rgba
    if (colorStr.startsWith('rgb')) {
      return colorStr.replace('rgb', 'rgba').replace(')', `, ${alpha})`);
    }
    // If hex, convert to rgba
    if (colorStr.startsWith('#')) {
      const r = parseInt(colorStr.slice(1, 3), 16);
      const g = parseInt(colorStr.slice(3, 5), 16);
      const b = parseInt(colorStr.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    // Fallback
    return colorStr;
  };

  /**
   * Draw pattern with audio-reactive effects
   */
  const drawPattern = (ctx: CanvasRenderingContext2D, audioIntensity: number = 0) => {
    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Apply transformations
    ctx.save();
    ctx.translate(width / 2, height / 2);

    // Pattern-specific fit scaling so geometry better fills the available canvas area.
    const patternFitScale: Record<string, number> = {
      'metatrons-cube': 2.1,
      'flower-of-life': 1.45,
      'seed-of-life': 1.45,
      'fruit-of-life': 1.55,
      'chladni-1-1': 1.35,
      'chladni-2-2': 1.35,
      'chladni-3-3': 1.35,
      'chladni-radial': 1.35,
      'chladni-hexagon': 1.3,
      'tree-of-life': 1.2,
      'merkaba': 1.3
    };
    const fitScale = patternFitScale[patternType] ?? 1.25;
    ctx.scale(fitScale, fitScale);
    
    // Audio-reactive zoom: pulse with audio intensity
    const audioZoom = 1 + (audioIntensity * 0.15); // 0-15% zoom increase
    ctx.scale(zoom * audioZoom, zoom * audioZoom);
    
    // Audio-reactive rotation: subtle rotation with audio
    const audioRotation = rotation + (audioIntensity * 5); // Up to 5 degrees
    ctx.rotate((audioRotation * Math.PI) / 180);
    ctx.translate(-width / 2, -height / 2);

    // Audio-reactive line width: thicker lines with more intensity
    const baseLineWidth = 2;
    const audioLineWidth = baseLineWidth + (audioIntensity * 3);
    ctx.lineWidth = audioLineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Audio-reactive color: brighter with more intensity
    const intensity = Math.min(audioIntensity, 1);
    const alpha = 0.5 + (intensity * 0.5); // 0.5 to 1.0 opacity
    ctx.strokeStyle = colorToRgba(color, alpha);
    ctx.fillStyle = colorToRgba(color, alpha);

    const centerX = width / 2;
    const centerY = height / 2;
    const baseRadius = Math.min(width, height) / 4;
    // Audio-reactive radius: expand with audio
    const radius = baseRadius * (1 + audioIntensity * 0.2);

    // Draw selected pattern
    switch (patternType) {
      // Sacred Geometry
      case 'flower-of-life':
        drawFlowerOfLifeReactive(ctx, centerX, centerY, radius / 2, audioIntensity);
        break;
      case 'seed-of-life':
      case 'egg-of-life':
        // Egg of Life is consolidated into Seed of Life (same 7-circle base form)
        drawSeedOfLifeReactive(ctx, centerX, centerY, radius / 2, audioIntensity);
        break;
      case 'fruit-of-life':
        drawFruitOfLifeReactive(ctx, centerX, centerY, radius / 2, audioIntensity);
        break;
      case 'vesica-piscis':
        drawVesicaPiscisReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'metatrons-cube':
        drawMetatronsCubeReactive(ctx, centerX, centerY, radius / 3, audioIntensity);
        break;
      case 'sri-yantra':
        drawSriYantraReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'tree-of-life':
        drawTreeOfLifeReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'merkaba':
        drawMerkabaReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      // Geometric Patterns
      case 'hexagon':
        drawHexagonReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'pentagon':
        drawPentagonReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'octagon':
        drawOctagonReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'dodecagon':
        drawDodecagonReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'star-pentagram':
        drawStarPentagramReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'mandala':
        drawMandalaReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      // Chladni Patterns
      case 'chladni-1-1':
        drawChladni11Reactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'chladni-2-2':
        drawChladni22Reactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'chladni-3-3':
        drawChladni33Reactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'chladni-hexagon':
        drawChladniHexagonReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'chladni-radial':
        drawChladniRadialReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      // Solfeggio Frequencies (use simple geometric representations)
      case 'solfeggio-174':
      case 'solfeggio-285':
      case 'solfeggio-417':
      case 'solfeggio-528':
      case 'solfeggio-639':
      case 'solfeggio-741':
      case 'solfeggio-852':
      case 'solfeggio-963':
        drawSolfeggioReactive(ctx, centerX, centerY, radius, audioIntensity, patternType);
        break;
      // Natural Patterns
      case 'golden-spiral':
        drawGoldenSpiralReactive(ctx, centerX, centerY, radius * 2, audioIntensity);
        break;
      case 'spiral-galaxy':
        drawSpiralGalaxyReactive(ctx, centerX, centerY, radius * 2, audioIntensity);
        break;
      case 'torus':
        drawTorusReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'snowflake':
        drawSnowflakeReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'water-cymatics':
        drawWaterCymaticsReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      case 'yin-yang':
        drawYinYangReactive(ctx, centerX, centerY, radius, audioIntensity);
        break;
      default:
        drawFlowerOfLifeReactive(ctx, centerX, centerY, radius / 2, audioIntensity);
    }

    ctx.restore();
  };

  /**
   * Main render function with real-time audio reactivity
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', {
      willReadFrequently: false,
      alpha: true
    });
    if (!ctx) return;
    
    // High-quality smoothing improves large geometric line rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Real-time animation loop when audio is playing
    if (isPlaying && audioData) {
      const animate = () => {
        if (!isPlaying || !audioData) {
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
          }
          return;
        }

        drawPattern(ctx, audioData.averageAmplitude);
        animationFrameRef.current = requestAnimationFrame(animate);
      };

      animate();
    } else {
      drawPattern(ctx, audioData?.averageAmplitude || 0);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [patternType, color, audioData, isPlaying, width, height]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          border: `2px solid ${color}40`,
          borderRadius: '12px',
          background: 'rgba(2, 6, 23, 0.95)',
          maxWidth: '100%',
          height: 'auto'
        }}
      />
    </div>
  );
};

export default SacredGeometryCanvas;

